import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { GraduationCap, Users, BookOpen, UserCheck, Loader2, Shield, School, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // School code step
  const [step, setStep] = useState('school'); // 'school' or 'credentials'
  const [schoolCode, setSchoolCode] = useState('');
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [verifyingSchool, setVerifyingSchool] = useState(false);
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Super Admin login
  const [isSuperAdminLogin, setIsSuperAdminLogin] = useState(false);

  const handleVerifySchool = async (e) => {
    e.preventDefault();
    if (!schoolCode.trim()) {
      toast.error('Please enter school code');
      return;
    }
    
    setVerifyingSchool(true);
    try {
      const response = await axios.get(`${API}/auth/verify-school/${schoolCode.toUpperCase()}`);
      setSchoolInfo(response.data);
      setStep('credentials');
      toast.success(`Welcome to ${response.data.name}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid school code');
    } finally {
      setVerifyingSchool(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);
    const result = await login(loginEmail, loginPassword, isSuperAdminLogin ? null : schoolCode.toUpperCase());
    setIsLoading(false);
    
    if (result.success) {
      toast.success('Welcome back!');
      if (result.user.role === 'superadmin') {
        navigate('/superadmin');
      } else {
        navigate(`/${result.user.role}`);
      }
    } else {
      toast.error(result.error);
    }
  };

  const handleSuperAdminToggle = () => {
    setIsSuperAdminLogin(!isSuperAdminLogin);
    setStep(isSuperAdminLogin ? 'school' : 'credentials');
    setSchoolInfo(null);
    setSchoolCode('');
  };

  const roleIcons = {
    admin: <Users className="w-5 h-5" />,
    teacher: <BookOpen className="w-5 h-5" />,
    student: <GraduationCap className="w-5 h-5" />,
    parent: <UserCheck className="w-5 h-5" />
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/26694112/pexels-photo-26694112.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="School"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 to-secondary/60 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <GraduationCap className="w-20 h-20 mx-auto mb-6" />
            <h1 className="text-5xl font-heading font-bold mb-4">EduTrack</h1>
            <p className="text-xl opacity-90 max-w-md">
              Comprehensive School Management System for Modern Education
            </p>
            <div className="mt-8 p-4 bg-white/10 rounded-lg backdrop-blur-sm">
              <p className="text-sm">Multi-School SaaS Platform</p>
              <p className="text-xs mt-1 opacity-75">Each school gets their own secure environment</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-2 text-primary">
              <GraduationCap className="w-10 h-10" />
              <span className="text-3xl font-heading font-bold">EduTrack</span>
            </div>
          </div>

          {/* Super Admin Toggle */}
          <div className="flex justify-end mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSuperAdminToggle}
              className="text-xs"
              data-testid="superadmin-toggle"
            >
              <Shield className="h-3 w-3 mr-1" />
              {isSuperAdminLogin ? 'School Login' : 'Super Admin'}
            </Button>
          </div>

          {isSuperAdminLogin ? (
            // Super Admin Login
            <Card className="border border-border">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <CardTitle className="text-2xl font-heading">Super Admin</CardTitle>
                </div>
                <CardDescription>
                  Access the SaaS admin panel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      placeholder="superadmin@edutrack.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      data-testid="superadmin-email-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password">Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      placeholder="Enter password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      data-testid="superadmin-password-input"
                      className="h-11"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11" 
                    disabled={isLoading}
                    data-testid="superadmin-login-button"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Access Admin Panel'
                    )}
                  </Button>
                </form>
                
                <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    Demo: superadmin@edutrack.com / SuperAdmin@123
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : step === 'school' ? (
            // Step 1: School Code
            <Card className="border border-border">
              <CardHeader className="space-y-1">
                <div className="flex items-center gap-2">
                  <School className="h-5 w-5 text-primary" />
                  <CardTitle className="text-2xl font-heading">Enter School Code</CardTitle>
                </div>
                <CardDescription>
                  Enter your school's unique code to proceed
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleVerifySchool} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="school-code">School Code</Label>
                    <Input
                      id="school-code"
                      type="text"
                      placeholder="e.g., DEMO"
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                      data-testid="school-code-input"
                      className="h-11 text-center text-lg font-mono tracking-wider"
                      maxLength={10}
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11" 
                    disabled={verifyingSchool}
                    data-testid="verify-school-button"
                  >
                    {verifyingSchool ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm font-medium mb-2">Demo School Code:</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSchoolCode('DEMO')}
                    className="font-mono"
                    data-testid="demo-school-button"
                  >
                    DEMO
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            // Step 2: Credentials
            <Card className="border border-border">
              <CardHeader className="space-y-1">
                {schoolInfo && (
                  <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg mb-2">
                    <School className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium text-sm">{schoolInfo.name}</p>
                      <p className="text-xs text-muted-foreground">Code: {schoolInfo.code}</p>
                    </div>
                  </div>
                )}
                <CardTitle className="text-2xl font-heading">Sign In</CardTitle>
                <CardDescription>
                  Enter your credentials to access your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="name@school.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      data-testid="login-email-input"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <a
                        href="/forgot-password"
                        className="text-xs text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </a>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      data-testid="login-password-input"
                      className="h-11"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      type="button"
                      variant="outline"
                      onClick={() => { setStep('school'); setSchoolInfo(null); }}
                      className="h-11"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 h-11" 
                      disabled={isLoading}
                      data-testid="login-submit-button"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </div>
                </form>

                {/* Quick Login Buttons */}
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground text-center mb-4">Demo Accounts</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { role: 'admin', email: 'admin@school.com', pass: 'Admin@123' },
                      { role: 'teacher', email: 'john@school.com', pass: 'teacher123' },
                      { role: 'student', email: 'alice@school.com', pass: 'student123' },
                      { role: 'parent', email: 'alice.parent@email.com', pass: 'parent123' }
                    ].map((demo) => (
                      <Button
                        key={demo.role}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setLoginEmail(demo.email);
                          setLoginPassword(demo.pass);
                        }}
                        className="flex items-center gap-2 capitalize"
                        data-testid={`demo-${demo.role}-button`}
                      >
                        {roleIcons[demo.role]}
                        {demo.role}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
